/**
 * SpeechSideBar: 대화기록 / 음성인식 패널.
 *
 * v1 원본: RenewalComponents/ChatHistory/ChatHistoryPresenter.js
 *   <div className="new_chat inst_tabs">
 *     <div className="chat_tabs">...</div>
 *     <div className="chatbox chatbox--record">...</div>
 *   </div>
 *
 * CSS 셀렉터: .rtc_new3 .sidebar.new2 .new_chat ...
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { captionActions, type CaptionCurrentText, type CaptionEntry } from '@/store/slices/captionSlice';
import { uiActions } from '@/store/slices/uiSlice';
import { voiceNoteActions } from '@/store/slices/voiceNoteSlice';
import { DEFAULT_PROFILE, toAbsoluteProfileUrl, toDisplayProfileUrl } from '@/utils/profileUtils';
import { formatTime12h } from '@/utils/formatUtils';
import VirtualChatList from '@/components/SVSideBar/VirtualChatList';
import TypewriterText, { InterimBubble, isFreshCaptionTimestamp } from '@/components/TypewriterText/TypewriterText';

type SpeechTimelineItem =
  | { type: 'entry'; id: string; entry: CaptionEntry }
  | { type: 'current'; id: string; current: CaptionCurrentText };

const SpeechSideBar: React.FC = () => {
  const dispatch = useAppDispatch();
  const caption = useAppSelector((s) => s.caption);
  const local = useAppSelector((s) => s.local);
  const participants = useAppSelector((s) => s.participants);
  const currentCaptions = useMemo(
    () => Object.values(caption.currentTexts).sort((a, b) => a.updatedAt - b.updatedAt),
    [caption.currentTexts]
  );
  const timelineItems = useMemo<SpeechTimelineItem[]>(() => [
    ...caption.entries.map((entry) => ({ type: 'entry' as const, id: entry.id, entry })),
    ...currentCaptions.map((current) => ({ type: 'current' as const, id: `current-${current.speakerId}`, current })),
  ], [caption.entries, currentCaptions]);
  const hasCaptionTimeline = timelineItems.length > 0;
  const showEmpty = !caption.isEnabled && !hasCaptionTimeline;

  const settingRef = useRef<HTMLDivElement>(null);
  const [isSettingOpen, setIsSettingOpen] = useState(false);
  const resolveProfileUrl = (url?: string | null) => toAbsoluteProfileUrl(url) ?? '';

  // 외부 클릭으로 설정 레이어 닫기
  useEffect(() => {
    if (!isSettingOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (settingRef.current && !settingRef.current.contains(e.target as Node)) {
        setIsSettingOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSettingOpen]);

  const getSenderInfo = (senderId: string, senderName: string, fallbackProfileUrl?: string) => {
    if (senderId === local.jitsiId || senderId === local.userId) {
      return {
        name: local.userName || local.nickname || local.userId,
        profileUrl: toDisplayProfileUrl(local.profileUrl, local.avatarUrl) ?? '',
      };
    }
    const p = participants.participants[senderId];
    if (p) {
      return {
        name: p.displayName || p.nickname,
        profileUrl: toDisplayProfileUrl(p.profileUrl, p.avatarUrl) ?? '',
      };
    }
    return { name: senderName, profileUrl: resolveProfileUrl(fallbackProfileUrl) || '' };
  };

  const getCurrentSenderInfo = (current: CaptionCurrentText) => {
    if (current.speakerId === local.jitsiId || current.speakerId === local.userId) {
      return {
        name: local.userName || local.nickname || local.userId,
        profileUrl: toDisplayProfileUrl(local.profileUrl, local.avatarUrl) ?? '',
      };
    }

    const p = participants.participants[current.speakerId];
    if (p) {
      return {
        name: p.displayName || p.nickname || current.speakerName,
        profileUrl: toDisplayProfileUrl(p.profileUrl || current.profileUrl, p.avatarUrl) ?? '',
      };
    }

    return {
      name: current.speakerName || current.nickname || current.speakerId,
      profileUrl: resolveProfileUrl(current.profileUrl),
    };
  };

  const handleEnableSpeech = () => {
    dispatch(captionActions.setEnabled(true));
    dispatch(uiActions.setCaptionEnabled(true));
    dispatch(voiceNoteActions.setEnabled(true));
  };

  const handleToggleSpeech = () => {
    const next = !caption.isEnabled;
    dispatch(captionActions.setEnabled(next));
    dispatch(uiActions.setCaptionEnabled(next));
    dispatch(voiceNoteActions.setEnabled(next));
  };

  const getSystemRecordText = (entry: CaptionEntry) => {
    const { name: sysName } = getSenderInfo(entry.userId, entry.userName);
    switch (entry.systemMessage) {
      case 'START_STT':
        return <><em>{sysName}</em>님의 발언이 기록됩니다.</>;
      case 'END_STT':
        return <><em>{sysName}</em>님의 발언이 더이상 기록되지 않습니다.</>;
      default:
        return entry.text || entry.systemMessage || '시스템 메시지';
    }
  };

  const isLocalTimelineItem = (item: SpeechTimelineItem | undefined) => {
    if (!item) return false;
    if (item.type === 'entry') {
      return item.entry.userId === local.jitsiId || item.entry.userId === local.userId;
    }
    return item.current.speakerId === local.jitsiId || item.current.speakerId === local.userId;
  };

  const renderEntry = (entry: CaptionEntry) => {
    if (entry.isSystem) {
      return (
        <div className="chatitem_recordLine">
          <span className="chatitem_recordLineText">
            {getSystemRecordText(entry)}
          </span>
        </div>
      );
    }

    const isMe = entry.userId === local.jitsiId || entry.userId === local.userId;
    const { name, profileUrl } = getSenderInfo(entry.userId, entry.userName, entry.profileUrl);
    // 방금 확정된 발화만 interim에서 이어서 타이핑. 과거 히스토리/스크롤백은 즉시 렌더.
    // final은 레인을 읽기만(laneWrite=false) — 다음 발화 interim의 이어치기 seed를 오염시키지 않는다
    const entryText = (
      <TypewriterText
        text={entry.text}
        animKey={`speech:${entry.userId}`}
        enabled={isFreshCaptionTimestamp(entry.timestamp)}
        laneWrite={false}
      />
    );

    return isMe ? (
      <div className="chatitem mychat">
        <div className="context_new">
          <span className="context_time">{formatTime12h(entry.timestamp)}</span>
          <p className="context_text">{entryText}</p>
        </div>
      </div>
    ) : (
      <div className="chatitem">
        <div className="profile">
          {profileUrl ? (
            <img src={profileUrl} alt="프로필 이미지" />
          ) : (
            <div className="common_coProfileBox common_coProfileBox--icon" style={{ background: '#c1cbdd', marginRight: 8 }}>
              <div className="common_coProfileBox__imgBox" style={{ backgroundImage: `url("${DEFAULT_PROFILE}")` }} />
            </div>
          )}
          <span className="name ellipsis">{name}</span>
        </div>
        <div className="context_new">
          <p className="context_text">{entryText}</p>
          <span className="context_time">{formatTime12h(entry.timestamp)}</span>
        </div>
      </div>
    );
  };

  const renderCurrent = (current: CaptionCurrentText) => {
    const isMe = current.speakerId === local.jitsiId || current.speakerId === local.userId;
    const { name, profileUrl } = getCurrentSenderInfo(current);
    const currentText = (
      <TypewriterText text={current.text} animKey={`speech:${current.speakerId}`} />
    );

    return isMe ? (
      <div className="chatitem mychat">
        <div className="context_new">
          <span className="context_time">
            <span className="rtcNew_chatProg">
              <span className="rtcNew_chatProgItem1" />
              <span className="rtcNew_chatProgItem2" />
              <span className="rtcNew_chatProgItem3" />
            </span>
          </span>
          <InterimBubble className="context_text">{currentText}</InterimBubble>
        </div>
      </div>
    ) : (
      <div className="chatitem">
        <div className="profile">
          {profileUrl ? (
            <img src={profileUrl} alt="프로필 이미지" />
          ) : (
            <div className="common_coProfileBox common_coProfileBox--icon" style={{ background: '#c1cbdd', marginRight: 8 }}>
              <div className="common_coProfileBox__imgBox" style={{ backgroundImage: `url("${DEFAULT_PROFILE}")` }} />
            </div>
          )}
          <span className="name ellipsis">{name}</span>
        </div>
        <div className="context_new">
          <InterimBubble className="context_text">{currentText}</InterimBubble>
          <span className="context_time">
            <span className="rtcNew_chatProg">
              <span className="rtcNew_chatProgItem1" />
              <span className="rtcNew_chatProgItem2" />
              <span className="rtcNew_chatProgItem3" />
            </span>
          </span>
        </div>
      </div>
    );
  };

  const renderTimelineItem = (item: SpeechTimelineItem) => (
    item.type === 'entry' ? renderEntry(item.entry) : renderCurrent(item.current)
  );

  const estimateTimelineItemSize = (index: number) => {
    const item = timelineItems[index];
    if (!item) return 88;
    if (item.type === 'entry' && item.entry.isSystem) return 44;
    if (item.type === 'entry' && (item.entry.userId === local.jitsiId || item.entry.userId === local.userId)) return 76;
    return 96;
  };

  return (
    <div className="new_chat inst_tabs">
      {/* v1: .chat_tabs 헤더 */}
      <div className="chat_tabs">
        <div className="chat_selection">
          <h2>대화기록</h2>
        </div>
        <div className="right_btns">
          {/* 설정 버튼 + 레이어 */}
          <div className={`right_btns__item${isSettingOpen ? ' open' : ''}`} ref={settingRef}>
            <button
              type="button"
              className={`chat_setting${isSettingOpen ? ' on' : ''}`}
              onClick={() => setIsSettingOpen(!isSettingOpen)}
            >
              <span className="blind">설정</span>
            </button>
            <div className="viewerHeader__layer">
              <strong className="viewerHeader__layerTitle">음성인식 언어 설정</strong>
              <ul className="viewerHeader__layerList">
                <li>
                  <dl className="viewerHeader__layerdl">
                    <dt>음성인식 설정</dt>
                    <dd>
                      <div className="common_onoff_switch small2 type_blue2">
                        <label>
                          <input
                            className="onoff_input"
                            type="checkbox"
                            checked={caption.isEnabled}
                            onChange={handleToggleSpeech}
                          />
                          <span className="switch_img" />
                          <span className="label_text on" />
                          <span className="label_text off" />
                        </label>
                      </div>
                    </dd>
                  </dl>
                </li>
              </ul>
            </div>
          </div>

          <button
            type="button"
            className="chat_closeBtn"
            onClick={() => dispatch(uiActions.setOpenSideBar(false))}
          >
            <span className="blind">닫기</span>
          </button>
        </div>
      </div>

      {showEmpty ? (
        <div className="chatbox chatbox--record chatbox--empty">
          {/* 음성인식 OFF: 빈 안내 */}
          <div className="empty_area">
            <span className="ico">
              <img src="https://static.wehago.com/imgs/common/ico_nodata138.png" width="42" alt="" />
            </span>
            <p className="empty_txt">
              음성인식을 사용하는 참석자가 없습니다.<br />
              음성인식을 사용하여 대화를 기록해보세요!
            </p>
            <button type="button" className="common_round_btn blue3" onClick={handleEnableSpeech}>
              대화 기록하기
            </button>
          </div>
        </div>
      ) : (
        <VirtualChatList
          className="chatbox chatbox--record"
          estimateSize={estimateTimelineItemSize}
          forceScrollToBottom={isLocalTimelineItem(timelineItems[timelineItems.length - 1])}
          getItemKey={(item) => item.id}
          items={timelineItems}
          renderItem={renderTimelineItem}
          rowGap={16}
        />
      )}
    </div>
  );
};

export default SpeechSideBar;
